import { SetContextLink } from "@apollo/client/link/context";

/**
 * Apollo Link that attaches the `Idempotency-Key` HTTP header to outgoing
 * **mutation** requests when the caller has supplied an idempotency key in the
 * operation context.
 *
 * Backend (Express middleware + `mutation_idempotency` table) reads this header
 * and dedupes within the TTL window - replays of the same `(key, request_hash)`
 * return the cached response; replays with a different payload throw
 * `IDEMPOTENCY_CONFLICT`.
 *
 * Sits behind `RetryLink` in the chain so that retries of a failed mutation
 * re-emit the **same** key (preserving idempotency across network blips). The
 * link is a no-op for queries / subscriptions.
 *
 * @example
 * ```ts
 * const key = crypto.randomUUID();
 * await client.mutate({
 *   mutation: PAYMENTS_SAVE,
 *   variables: { input },
 *   context: { idempotencyKey: key },
 * });
 * ```
 *
 * Notes
 * - The `@envelop/idempotency` package does NOT exist on npm (404 as of 2026-05).
 *   This link is the project-local equivalent - Apollo v4 idiomatic, zero deps.
 * - Re-using the same key with a different payload is treated as a *conflict*
 *   on the backend, not silently overwritten. Callers must generate a fresh
 *   key per logical user intent (not per HTTP attempt).
 */
export const idempotencyLink = new SetContextLink(({ headers, idempotencyKey }, operation) => {
    const isMutation = operation.query.definitions.some(
        (d) => d.kind === "OperationDefinition" && d.operation === "mutation",
    );
    if (!isMutation || typeof idempotencyKey !== "string" || !idempotencyKey) {
        return { headers };
    }
    return {
        headers: {
            ...headers,
            "Idempotency-Key": idempotencyKey,
        },
    };
});
